import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { notifyFollowingAccount } from 'utils/NotificationHelper'
import ACCESS from 'utils/Access'

import app from 'containers/app'
import { epic as userPropertiesEpic } from 'app/modules/user/properties'
import * as SocketConstants from 'app/modules/socket/constants'
import * as UserActions from 'app/modules/user/actions'
import * as UserPropertiesActions from 'app/modules/user/properties/actions'
import * as ProfileActions from 'app/modules/profile/actions'
import * as GateSelectors from 'app/modules/gates/selectors'
import { api as UserAPI, parseUser } from 'app/modules/user/api'
import { api as ProfileAPI } from 'app/modules/profile/api'
import { currentUser, isUpdatingUser } from 'app/modules/user/selectors'

const fetchClearbitForEmail = action$ => {
  return action$
    .ofType(UserActions.fetchClearbitForEmail.toString())
    .pluck('payload')
    .mergeMap(email => {
      return UserAPI.fetchClearbitForEmail(email)
        .map(UserActions.fetchClearbitForEmailSuccess)
        .startWith(UserActions.fetchClearbitForEmailPending({ email }))
        .catch(error => {
          return Observable.of(UserActions.fetchClearbitForEmailFailed({ email, error }))
        })
    })
}

const fetchGoogleScopes = action$ => {
  return action$.ofType(UserActions.fetchGoogleScopes.toString()).mergeMap(() => {
    return UserAPI.fetchGoogleScopes()
      .map(UserActions.fetchGoogleScopesSuccess)
      .startWith(UserActions.fetchGoogleScopesPending())
      .catch(error => {
        return Observable.of(UserActions.fetchGoogleScopesFailed({ error }))
      })
  })
}

const fetchUserProfile = action$ => {
  return action$
    .ofType(ProfileActions.fetchUserProfile.toString())
    .pluck('payload')
    .flatMap(userId => {
      return ProfileAPI.get(userId)
        .map(({ profile }) => ProfileActions.fetchUserProfileSuccess({ userId, profile }))
        .startWith(ProfileActions.fetchUserProfilePending())
        .catch(error => Observable.of(ProfileActions.fetchUserProfileFailed({ userId, error })))
    })
}

function fetchEmailSignature(action$, { getState }) {
  return action$
    .ofType(UserActions.fetchEmailSignature.toString())
    .pluck('payload')
    .mergeMap(() => {
      const user = currentUser(getState())
      return UserAPI.fetchEmailSignature(user.id)
        .map(response => UserActions.fetchEmailSignatureSuccess(response.emailSignature))
        .startWith(UserActions.fetchEmailSignaturePending())
        .catch(error => Observable.of(UserActions.fetchEmailSignatureFailed({ error })))
    })
}

// TODO: deprecated, remove once multiple signatures is ungated
const fetchOrgSignature = action$ =>
  action$
    .ofType(UserActions.fetchOrgSignature.toString())
    .pluck('payload')
    .mergeMap(({ userId }) =>
      UserAPI.fetchOrgSignature({ userId })
        .map(UserActions.fetchOrgSignatureSuccess)
        .startWith(UserActions.fetchOrgSignaturePending())
        .catch(err => Observable.of(UserActions.fetchOrgSignatureFailed({ err })))
    )

const fetchOrgMultiSignature = action$ =>
  action$
    .ofType(UserActions.fetchOrgMultiSignature.toString())
    .pluck('payload')
    .mergeMap(({ userId }) =>
      UserAPI.fetchOrgMultiSignature({ userId })
        .map(UserActions.fetchOrgMultiSignatureSuccess)
        .startWith(UserActions.fetchOrgMultiSignaturePending())
        .catch(err => Observable.of(UserActions.fetchOrgMultiSignatureFailed({ err })))
    )

function setEmailSignature(action$, { getState }) {
  return action$
    .ofType(UserActions.setEmailSignature.toString())
    .pluck('payload')
    .mergeMap(emailSignature => {
      const user = currentUser(getState())
      return UserAPI.setEmailSignature(user.id, emailSignature)
        .map(response => {
          app.actions.NotificationActions.pushNotification({
            content: `Email signature updated successfully!`,
            variant: 'green',
          })
          return UserActions.setEmailSignatureSuccess(response.emailSignature)
        })
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            content: `Email signature update failed. Please try again.`,
            variant: 'red',
          })
          return Observable.of(UserActions.setEmailSignatureFailed({ error }))
        })
    })
}

function pushUser(action$, { getState }) {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter(payload => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter(payload => payload.channelName === SocketConstants.SocketChannel.ORG_PRIVATE)
    .switchMap(({ channel, leaveChannel$ }) => {
      const currentUserId = currentUser(getState()).id

      return Observable.create(observer => {
        channel.on('user-new', payload => {
          app.actions.UserActions.userUpdated(payload.id, payload)
          observer.next(UserActions.receiveOrgUser(parseUser(payload)))
        })

        channel.on('user-updated', payload => {
          app.actions.UserActions.userUpdated(payload.id, payload)
          if (!isUpdatingUser(getState())) {
            observer.next(UserActions.receiveOrgUser(parseUser(payload)))
            if (payload.id === currentUserId) {
              observer.next(UserActions.receiveCurrentUser(parseUser(payload)))
            }
          }
        })
      }).takeUntil(leaveChannel$)
    })
}

function turnOffManualAway(action$, { getState }) {
  return action$
    .ofType(UserActions.turnOffManualAway.toString())
    .map(() => ({ userId: currentUser(getState()).id }))
    .mergeMap(({ userId }) => {
      return UserAPI.turnOffManualAway({ userId })
        .map(user => UserActions.turnOffManualAwaySuccess(user))
        .startWith(UserActions.turnOffManualAwayPending({ userId }))
        .catch(() => Observable.of(UserActions.turnOffManualAwayFailure({ userId })))
    })
}

function receiveUserAfterTurnOffManualAway(action$) {
  return action$
    .ofType(UserActions.turnOffManualAwaySuccess.toString())
    .pluck('payload')
    .map(user => UserActions.receiveCurrentUser(user))
}

function updateUserConversationLimitStatus(action$) {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter(payload => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter(payload => payload.channelName === SocketConstants.SocketChannel.USER)
    .switchMap(({ channel, leaveChannel$ }) => {
      return Observable.create(observer => {
        channel.on('user-convo-limit-update', payload => {
          observer.next(UserPropertiesActions.userAtConversationLimitUpdate(payload.body.hasUserReachedLimit))
        })
      }).takeUntil(leaveChannel$)
    })
}

function disconnectGmailAccount(action$) {
  return action$.ofType(UserActions.disconnectGmailAccount.toString()).switchMap(() => {
    return UserAPI.disconnectGmailAccount()
      .map(() => UserActions.disconnectGmailAccountSuccess())
      .startWith(UserActions.disconnectGmailAccountPending())
      .catch(() => Observable.of(UserActions.disconnectGmailAccountFailure()))
  })
}

const fetchIndividualConvoLimit = action$ => (
  action$
    .ofType(UserActions.fetchIndividualUserConvoLimit.toString())
    .pluck('payload')
    .mergeMap(({ userId }) => (
      UserAPI
        .fetchIndividualConvoLimit()
        .map((data) => UserActions.fetchIndividualUserConvoLimitSuccess({ data, userId }))
        .startWith(UserActions.fetchIndividualUserConvoLimitPending({ userId }))
        .catch(() => Observable.of(UserActions.fetchIndividualUserConvoLimitFailed({ userId })))
    ))
)

const updateIndividualConvoLimit = action$ => (
  action$
    .ofType(UserActions.updateIndividualUserConvoLimit.toString())
    .pluck('payload')
    .mergeMap(({ userId, limit, remove }) => (
      UserAPI
        .updateIndividualConvoLimit(limit, remove)
        .map(() => UserActions.updateIndividualUserConvoLimitSuccess({ data: { userId, convoLimit: limit }, userId }))
        .startWith(UserActions.updateIndividualUserConvoLimitPending({ userId }))
        .catch(() => Observable.of(UserActions.updateIndividualUserConvoLimitFailed({ userId })))
    ))
)

const updateUser = action$ => action$
  .ofType(UserActions.updateUser.toString())
  .pluck('payload')
  .mergeMap(({ user, originalUser, requiredPurchase }) => {
    return UserAPI.updateUser(user)
      .map(() => {
        if (!requiredPurchase) {
          app.actions.NotificationActions.pushNotification({
            content: `Successfully updated ${user.name || user.email}`,
            variant: 'green',
          })
        }
        return UserActions.updateUserSuccess(user)
      })
    .startWith(UserActions.updateUserPending(user))
    .catch(() => {
      app.actions.NotificationActions.pushNotification({
        content: `Failed to update ${user.name || user.email}`,
        variant: 'red',
      })
      return Observable.of(UserActions.updateUserFailed(originalUser))
    })
  })

export const epic = combineEpics(
  pushUser,
  userPropertiesEpic,
  setEmailSignature,
  fetchClearbitForEmail,
  fetchUserProfile,
  fetchEmailSignature,
  turnOffManualAway,
  receiveUserAfterTurnOffManualAway,
  fetchGoogleScopes,
  fetchOrgSignature,
  disconnectGmailAccount,
  fetchOrgMultiSignature,
  updateUserConversationLimitStatus,
  fetchIndividualConvoLimit,
  updateIndividualConvoLimit,
  updateUser
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/user/epic.js